// React imports
import React, { useRef, useState, useEffect } from "react";

// Icon imports
import { PaperAirplaneIcon, ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";

// Shared file upload utilities
import {
  ALLOWED_EXTENSIONS,
  ALLOWED_EXTENSIONS_DISPLAY,
  MAX_FILE_SIZE_MB,
  MAX_FILE_SIZE_BYTES,
  isFileTypeAllowed,
  formatFileSize,
} from './utils/fileUploadUtils';

// Build accept string for file input (only used here)
const ACCEPT_STRING = ALLOWED_EXTENSIONS.join(',');

// Redux imports
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";
import { sendMessage, updateUploadInTable } from "./ChatSlice_Store";
import { selectCurrentProjectId } from "./ProjectSlice_Store";
import { uploadFileToProject } from "./services/fileUploadService";

interface ChatInputProps {
  ariaLabel?: string;
  value?: string;
  onChange?: (value: string) => void;
}

// Default export of ChatInput_Component
export default function ChatInput_Component({ ariaLabel = "Message input field", value, onChange }: ChatInputProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [internalInputText, setInternalInputText] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [uploadError, setUploadError] = useState<string>("");
  const [uploadingFile, setUploadingFile] = useState<boolean>(false);
  const [fileTypeError, setFileTypeError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-dismiss file type error after 5 seconds
  useEffect(() => {
    if (fileTypeError) {
      const timer = setTimeout(() => setFileTypeError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [fileTypeError]);

  // Use controlled value if provided, otherwise use internal state
  const inputText = value !== undefined ? value : internalInputText;

  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const reduxAuth = useAppSelector(state => state.userAuthAndAdminSlice);
  const chatInputReady = useAppSelector(state => state.chatSlice.chatInputReady);
  const useCaseSelected = useAppSelector(state => state.chatSlice.useCaseSelected);
  const selectedUseCase = useAppSelector(state => state.chatSlice.selectedUseCase);
  const projectReady = Boolean(currentProjectId);
  const isUploadDisabledForUseCase = selectedUseCase === 'past-deals';
  const isUploadButtonBusy = uploadingFile || !projectReady;

  const getCurrentUserId = (): string => {
    if (reduxAuth?.isAuthenticated && reduxAuth?.userId) {
      return reduxAuth.userId;
    }
    if (reduxAuth?.isAuthenticated && reduxAuth?.userEmail) {
      return reduxAuth.userEmail;
    }
    return "test.user";
  };

  const shouldDismissVirtualKeyboard = (): boolean => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia("(pointer: coarse)").matches;
  };

  const dismissVirtualKeyboard = (): void => {
    if (!shouldDismissVirtualKeyboard()) {
      return;
    }
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
  };

  // Handle form submission (this will now handle clicks AND Enter key)
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    // Allow typing but prevent sending until project is ready, input is unlocked, and use case is selected
    if (inputText.trim() === "" || !projectReady || !chatInputReady || !useCaseSelected) return;

    dispatch(sendMessage(inputText.trim()));
    if (onChange) {
      onChange("");
    } else {
      setInternalInputText("");
    }
    // Dismiss the virtual keyboard on touch devices after sending
    dismissVirtualKeyboard();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    if (onChange) {
      onChange(e.target.value);
    } else {
      setInternalInputText(e.target.value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    // Enter without Shift submits the message
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (inputText.trim() !== "" && projectReady && chatInputReady && useCaseSelected) {
        dispatch(sendMessage(inputText.trim()));
        if (onChange) {
          onChange("");
        } else {
          setInternalInputText("");
        }
        // Dismiss the virtual keyboard on touch devices after sending
        dismissVirtualKeyboard();
      }
    }
    // Shift+Enter allows default behavior (new line)
  };

  const handleFileButtonClick = (): void => {
    if (!projectReady || uploadingFile || !useCaseSelected || isUploadDisabledForUseCase) {
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const resetFileInput = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadSelectedFile = async (selectedFile: File): Promise<void> => {
    setUploadError("");
    setUploadStatus("Getting upload URL...");
    setUploadingFile(true);

    // Generate unique ID even when multiple uploads start simultaneously
    const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    // Initialize upload in table
    dispatch(updateUploadInTable({
      uploadId,
      fileName: selectedFile.name,
      fileSize: selectedFile.size,
      progress: 0,
      status: "Preparing upload...",
      completed: false,
      error: false,
    }));

    try {
      const { filePath } = await uploadFileToProject({
        file: selectedFile,
        username: getCurrentUserId(),
        projectId: currentProjectId,
        onStatusChange: (status: string) => {
          if (status === "fetching_sas") {
            setUploadStatus("Getting upload URL...");
            dispatch(updateUploadInTable({
              uploadId,
              status: "Getting upload URL...",
            }));
          }
          if (status === "uploading") {
            setUploadStatus("Uploading file...");
            dispatch(updateUploadInTable({
              uploadId,
              status: "Uploading file...",
            }));
          }
        },
        onProgress: ({ percent }: { percent: number | null }) => {
          if (percent == null) return;
          dispatch(updateUploadInTable({
            uploadId,
            progress: percent,
          }));
        },
      });

      setUploadStatus("");

      if (currentProjectId && filePath) {
        dispatch(updateUploadInTable({
          uploadId,
          progress: 100,
          status: "Uploaded. Waiting for processing...",
          completed: false, // Don't mark as completed yet - wait for backend processing
        }));

        const controlMessage = `CONTROL_FILE_UPLOAD:${currentProjectId}:${filePath}`;
        dispatch(sendMessage(controlMessage));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setUploadError(errorMessage);
      setUploadStatus("");
      dispatch(updateUploadInTable({
        uploadId,
        status: `Upload failed: ${errorMessage}`,
        error: true,
      }));
    } finally {
      setUploadingFile(false);
      resetFileInput();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }

    const files = Array.from(selectedFiles);

    // Validate file types
    const invalidTypeFiles = files.filter(file => !isFileTypeAllowed(file.name));
    if (invalidTypeFiles.length > 0) {
      const invalidNames = invalidTypeFiles.map(f => f.name).join(', ');
      setFileTypeError(`Unsupported file type: ${invalidNames}. Allowed formats: ${ALLOWED_EXTENSIONS_DISPLAY}`);
      resetFileInput();
      return;
    }

    // Validate file sizes
    const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
      if (oversizedFiles.length === 1) {
        const file = oversizedFiles[0];
        setFileTypeError(`"${file.name}" (${formatFileSize(file.size)}) exceeds the ${MAX_FILE_SIZE_MB} MB limit. Please reduce the file size or split into smaller documents.`);
      } else {
        const fileList = oversizedFiles.map(f => `• ${f.name} (${formatFileSize(f.size)})`).join('\n');
        setFileTypeError(`${oversizedFiles.length} files exceed the ${MAX_FILE_SIZE_MB} MB limit:\n${fileList}`);
      }
      resetFileInput();
      return;
    }

    // Clear any previous error
    setFileTypeError("");

    // Upload all selected files in parallel
    const uploadPromises = files.map(file => uploadSelectedFile(file));
    await Promise.all(uploadPromises);
  };

  return (
    <div className="relative w-full bg-transparent border-t border-cvc-grey p-3 sm:p-4">
      {/* Speech bubble hint — visible only before a feature is selected */}
      {!useCaseSelected && (
        <div
          className="absolute pointer-events-none z-10 animate-in fade-in slide-in-from-bottom-2 duration-200 hidden sm:block"
          style={{ bottom: '100%', left: '0' }}
          role="status"
          aria-live="polite"
        >
          {/* Bubble box — SVG tail is absolutely positioned below this box */}
          <div className="inline-block bg-white rounded-xl px-4 py-2.5 font-cvcBody border-2 border-cvc-blue max-w-[calc(100vw-2rem)]">
            <span className="text-sm font-semibold text-cvc-blue">
              Select a feature above
            </span>
            {/* Downward-pointing SVG tail — same pattern as Tooltip component */}
            <svg
              width="20"
              height="10"
              viewBox="0 0 20 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute"
              style={{ bottom: '-10px', left: '16px' }}
              aria-hidden="true"
              focusable="false"
            >
              {/* Outer border path */}
              <path d="M 0 0 L 10 10 L 20 0 Z" fill="white" stroke="#0C4F88" strokeWidth="2" />
              {/* Inner white fill to cover the stroke on the top (flat) edge */}
              <path d="M 2 0 L 10 8 L 18 0 Z" fill="white" />
            </svg>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex items-center gap-2 sm:gap-3">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT_STRING}
          className="hidden"
          onChange={handleFileChange}
          disabled={!projectReady || uploadingFile}
        />

        <button
          type="button"
          onClick={handleFileButtonClick}
          disabled={!projectReady || uploadingFile || !useCaseSelected || isUploadDisabledForUseCase}
          className="bg-white border border-cvc-grey text-cvc-text hover:text-cvc-blue hover:border-cvc-blue disabled:text-cvc-grey disabled:border-cvc-grey disabled:cursor-not-allowed p-3 sm:p-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-cvc-blue focus:ring-opacity-50 transition-colors duration-200 flex items-center justify-center min-h-[48px] min-w-[48px] sm:min-h-[52px] sm:min-w-[52px]"
          aria-label="Upload file"
          title={!projectReady ? "Waiting for project setup" : !useCaseSelected ? "Please select a use case to start" : isUploadDisabledForUseCase ? "File upload is not available for Deal Search" : undefined}
        >
          {isUploadButtonBusy ? (
            <svg
              className={`animate-spin h-5 w-5 ${projectReady ? "text-cvc-blue" : "text-cvc-grey"}`}
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <ArrowUpTrayIcon className="h-5 w-5 sm:h-6 sm:w-6" />
          )}
        </button>

        {/* Text input field - mobile optimized */}
        <textarea
          value={inputText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Ask DealOS..."
          disabled={!useCaseSelected}
          rows={1}
          className={`flex-1 px-3 sm:px-4 py-3 sm:py-4 border rounded-lg focus:outline-none text-base min-h-[48px] sm:min-h-[52px] max-h-[132px] touch-manipulation font-cvcBody resize-none overflow-y-auto ${
            useCaseSelected
              ? "border-cvc-grey focus:ring-2 focus:ring-cvc-blue focus:border-transparent text-cvc-text placeholder-cvc-text/50 bg-white"
              : "border-cvc-grey bg-white text-gray-400 placeholder-gray-400 cursor-not-allowed"
          }`}
          style={{
            fieldSizing: 'content'
          } as React.CSSProperties}
          aria-label={ariaLabel}
          autoComplete="off"
          autoCapitalize="sentences"
          autoCorrect="on"
        />

        {/* Send button - touch-friendly size */}
        <button
          type="submit"
          disabled={inputText.trim() === "" || !projectReady || !chatInputReady || !useCaseSelected}
          className="bg-cvc-blue hover:opacity-90 disabled:bg-cvc-grey disabled:cursor-not-allowed text-white font-medium p-3 sm:p-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-cvc-blue focus:ring-opacity-50 transition-all duration-200 flex items-center justify-center min-h-[48px] min-w-[48px] sm:min-h-[52px] sm:min-w-[52px] touch-manipulation"
          aria-label="Send message button"
          aria-disabled={!chatInputReady || !useCaseSelected}
          aria-busy={!chatInputReady}
          title={!projectReady ? "Waiting for project setup" : !useCaseSelected ? "Please select a use case to start" : undefined}
        >
          <PaperAirplaneIcon className="h-5 w-5 sm:h-6 sm:w-6" />
        </button>
      </form>

      {/* AI disclaimer */}
      <p className="text-xs text-center text-gray-400 mt-2">
        DealOS can make mistakes. Always check source documents.
      </p>

      {(uploadStatus || uploadError) && (
        <div className="mt-2">
          {uploadStatus && (
            <p className="text-xs text-cvc-green">{uploadStatus}</p>
          )}
          {uploadError && (
            <p className="text-xs text-cvc-coral">{uploadError}</p>
          )}
        </div>
      )}

      {/* File type error popover */}
      {fileTypeError && (
        <div className="absolute bottom-full left-0 right-0 mb-2 mx-3 sm:mx-4 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 shadow-lg flex items-start gap-2">
            <div className="flex-1">
              <p className="text-sm text-red-800 font-medium">Invalid file format</p>
              <p className="text-xs text-red-600 mt-1">{fileTypeError}</p>
            </div>
            <button
              type="button"
              onClick={() => setFileTypeError("")}
              className="text-red-400 hover:text-red-600 p-1"
              aria-label="Dismiss error"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
